<script setup lang="ts">
	export type BusinessProfileSchema = {
		[id: string]: {
			value: string
			iconDir: string
			iconName: string
		}
	}

	defineProps<{
		profiles?: {
			[id: string]: BusinessProfileSchema
		}
	}>()
	const { accountInfo } = useAuth()
	const userInfo = computed(() => {
		return accountInfo.value
	})
	const businessName = ref('Business Name')

	const username = computed(() => {
		return userInfo.value?.customer?.name || '--'
	})
	const initials = computed(() => {
		const nameList = username.value?.split(' ')
		if (nameList.length) {
			return `${nameList[0] ? nameList[0][0] : ''} ${nameList[1] ? nameList[1][0] : ''}`
		}
		return '--'
	})
</script>

<template>
	<section class="space-y-6">
		<shared-card
			wrapper-class="flex justify-between max-w-full items-center flex-wrap md:border border-b border-gray-200"
		>
			<div class="flex w-full items-center gap-8 px-5 md:px-0">
				<div
					class="relative flex h-16 w-16 flex-none items-center justify-center rounded-full bg-orange-200 md:h-[100px] md:w-[100px]"
				>
					<span class="font-semibold uppercase text-blue-950 md:text-xl">
						{{ initials }}
					</span>
				</div>

				<div class="flex w-full flex-wrap items-center justify-between gap-2">
					<div>
						<h1 class="mb-0 pb-0 text-sm font-medium capitalize text-blue-950 md:text-xl">
							{{ username ?? username }}
						</h1>
						<p class="mt-0 pt-0 font-medium text-gray-400">
							{{ businessName }}
						</p>
					</div>

					<nuxt-link
						v-if="userInfo?.role === 'admin'"
						:to="{
							path: '/settings/edit',
							query: {
								type: 'business'
							}
						}"
						class="max-w-[180px] rounded-md bg-primary-2 px-6 py-2 font-medium !text-white transition hover:bg-primary-500 sm:w-full sm:py-3 sm:text-center"
					>
						Edit Profile
					</nuxt-link>
				</div>
			</div>
		</shared-card>

		<shared-card
			wrapper-class="max-w-full"
			default-class="grid grid-cols-1  items-start sm:grid-cols-2 gap-6 md:border md:px-14 gap-y-3 sm:py-[70px]"
		>
			<div>
				<h4 class="mb-5 text-xs font-medium leading-tight text-blue-950 md:hidden">Business Details</h4>

				<div class="space-y-6 md:space-y-8">
					<div
						v-for="(value, key, index) in profiles?.business_details || {}"
						:key="index"
						class="flex items-center gap-x-4"
					>
						<div>
							<lazy-tools-icon
								:dir="value.iconDir"
								:icon="value.iconName"
								path-class="stroke-purple-500"
								:width="20"
								:height="20"
							/>
						</div>

						<div>
							<p class="my-1 pb-0 text-xs font-light text-gray-500">
								{{ key }}
							</p>

							<h1 class="mt-0 pt-0 text-sm font-medium text-gray-600">
								{{ value.value }}
							</h1>
						</div>
					</div>
				</div>
			</div>

			<div>
				<h4 class="mb-5 text-xs font-medium leading-tight text-blue-950 md:hidden">Personal Details</h4>

				<div class="space-y-6 md:space-y-8">
					<div
						v-for="(value, key, index) in profiles?.business_location || {}"
						:key="index"
						class="flex items-center gap-x-4"
					>
						<div>
							<lazy-tools-icon :dir="value.iconDir" :icon="value.iconName" />
						</div>

						<div>
							<p class="my-1 pb-0 text-xs font-light text-gray-500">
								{{ key }}
							</p>

							<h1 class="mt-0 pt-0 text-sm font-medium text-gray-600">
								{{ value.value }}
							</h1>
						</div>
					</div>
				</div>
			</div>
		</shared-card>
	</section>
</template>
